import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../components/ui/accordion';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { CalendarDays, Download, Layers, LayoutList, LayoutGrid, RefreshCcw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_META = {
  expense: { label: 'Op-Ex', color: 'bg-orange-600' },
  stock_cash: { label: 'Stock (Cash)', color: 'bg-indigo-600' },
  salary: { label: 'Salary', color: 'bg-emerald-600' },
  debt: { label: 'Debt', color: 'bg-rose-600' },
};

const fmtR = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Auto Spares — Expenses by Month tab. Merges op-ex, cash stock,
 *  salaries and debt repayments per month with three view modes. */
export default function ExpensesByMonthPanel() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [category, setCategory] = useState('__all__');
  const [view, setView] = useState('accordion'); // accordion | flat | pivot
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { year };
      if (category && category !== '__all__') params.category = category;
      const r = await axios.get(`${API_URL}/api/auto-spares/expenses-by-month`, { headers, params });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load expenses'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, category]);

  const downloadPdf = async () => {
    try {
      const params = { year };
      if (category && category !== '__all__') params.category = category;
      const r = await axios.get(`${API_URL}/api/auto-spares/expenses-by-month/pdf`,
        { headers, params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `expenses-by-month-${year}${category !== '__all__' ? `-${category}` : ''}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('PDF export failed'); }
  };

  const years = useMemo(() => {
    const y = now.getFullYear();
    return [y + 1, y, y - 1, y - 2, y - 3];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flatRows = useMemo(() => {
    if (!data) return [];
    const rows = [];
    for (const m of data.months) {
      for (const t of m.transactions) rows.push({ ...t, month: m.month });
    }
    return rows;
  }, [data]);

  const pivotCategories = useMemo(() => {
    if (!data) return [];
    const set = new Set();
    for (const m of data.months) for (const t of m.transactions) set.add(t.category);
    return [...set].sort();
  }, [data]);

  const pivotCell = (cat, m) => {
    const total = m.transactions.filter(t => t.category === cat)
      .reduce((s, t) => s + t.amount, 0);
    return total;
  };

  return (
    <div className="space-y-4" data-testid="expenses-by-month-panel">
      {/* Toolbar */}
      <Card className="bg-slate-800/60 border-slate-700 text-white">
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5">
            <CalendarDays className="h-4 w-4 text-slate-400" />
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-28 bg-slate-900 border-slate-600" data-testid="ebm-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-52 bg-slate-900 border-slate-600" data-testid="ebm-category-select">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All categories</SelectItem>
              {(data?.categories || []).map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="ml-auto flex items-center gap-1">
            <div className="rounded-md border border-slate-600 overflow-hidden bg-slate-900" data-testid="ebm-view-toggle">
              <button className={`px-2.5 py-1.5 text-xs flex items-center gap-1 ${view === 'accordion' ? 'bg-slate-700' : ''}`}
                onClick={() => setView('accordion')} data-testid="ebm-view-accordion">
                <Layers className="h-3.5 w-3.5" /> Accordion
              </button>
              <button className={`px-2.5 py-1.5 text-xs flex items-center gap-1 ${view === 'flat' ? 'bg-slate-700' : ''}`}
                onClick={() => setView('flat')} data-testid="ebm-view-flat">
                <LayoutList className="h-3.5 w-3.5" /> Flat
              </button>
              <button className={`px-2.5 py-1.5 text-xs flex items-center gap-1 ${view === 'pivot' ? 'bg-slate-700' : ''}`}
                onClick={() => setView('pivot')} data-testid="ebm-view-pivot">
                <LayoutGrid className="h-3.5 w-3.5" /> Pivot
              </button>
            </div>
            <Button variant="outline" size="sm" onClick={load} className="border-slate-600 text-slate-200 bg-slate-900 hover:bg-slate-700" data-testid="ebm-refresh">
              <RefreshCcw className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" onClick={downloadPdf} className="bg-orange-600 hover:bg-orange-700 gap-1" data-testid="ebm-pdf">
              <Download className="h-3.5 w-3.5" /> PDF
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Totals summary */}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2" data-testid="ebm-totals">
          {Object.entries(KIND_META).map(([k, meta]) => (
            <Card key={k} className={`bg-gradient-to-br ${meta.color} to-black/40 border-0 text-white`}>
              <CardContent className="p-3">
                <div className="text-[10px] uppercase opacity-80">{meta.label}</div>
                <div className="text-lg font-bold" data-testid={`ebm-total-${k}`}>{fmtR(data.by_kind_total[k])}</div>
              </CardContent>
            </Card>
          ))}
          <Card className="bg-gradient-to-br from-slate-700 to-slate-900 border-0 text-white">
            <CardContent className="p-3">
              <div className="text-[10px] uppercase opacity-80">Grand Total {year}</div>
              <div className="text-lg font-extrabold" data-testid="ebm-grand-total">{fmtR(data.grand_total)}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Views */}
      {loading ? (
        <div className="text-sm text-slate-400 text-center py-6">Loading…</div>
      ) : !data ? null : view === 'accordion' ? (
        <Accordion type="multiple" className="space-y-1" data-testid="ebm-accordion">
          {data.months.filter(m => m.transactions.length > 0).map((m) => (
            <AccordionItem key={m.month} value={m.month} className="border rounded-md bg-white">
              <AccordionTrigger className="px-3 py-2 hover:no-underline" data-testid={`ebm-month-${m.month}`}>
                <div className="flex flex-1 items-center justify-between pr-2">
                  <div className="flex items-center gap-3 text-left">
                    <span className="font-semibold">{m.label}</span>
                    <span className="text-xs text-slate-500">{m.transactions.length} txn</span>
                    <div className="hidden sm:flex items-center gap-1">
                      {Object.entries(m.by_kind).filter(([, v]) => v > 0).map(([k, v]) => (
                        <Badge key={k} className={`${KIND_META[k].color} text-white text-[10px]`}>
                          {KIND_META[k].label}: {fmtR(v)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <span className="font-bold text-slate-900">{fmtR(m.total)}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-3 pb-3">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {m.transactions.map((t, i) => (
                      <TableRow key={`${m.month}-${i}`} data-testid={`ebm-row-${m.month}-${i}`}>
                        <TableCell className="font-mono text-xs">{t.date}</TableCell>
                        <TableCell><Badge className={`${KIND_META[t.kind].color} text-white text-[10px]`}>{KIND_META[t.kind].label}</Badge></TableCell>
                        <TableCell>{t.category}</TableCell>
                        <TableCell className="text-xs">{t.description}</TableCell>
                        <TableCell className="text-right font-mono font-semibold">{fmtR(t.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </AccordionContent>
            </AccordionItem>
          ))}
          {data.months.every(m => m.transactions.length === 0) && (
            <Card><CardContent className="p-6 text-center text-slate-500 italic">No expenses recorded for {year}.</CardContent></Card>
          )}
        </Accordion>
      ) : view === 'flat' ? (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table data-testid="ebm-flat-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Month</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {flatRows.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center text-xs text-slate-500 py-6 italic">No transactions.</TableCell></TableRow>
                ) : flatRows.map((t, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-mono text-xs">{t.date}</TableCell>
                    <TableCell className="font-mono text-xs">{t.month}</TableCell>
                    <TableCell><Badge className={`${KIND_META[t.kind].color} text-white text-[10px]`}>{KIND_META[t.kind].label}</Badge></TableCell>
                    <TableCell>{t.category}</TableCell>
                    <TableCell className="text-xs">{t.description}</TableCell>
                    <TableCell className="text-right font-mono">{fmtR(t.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table data-testid="ebm-pivot-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[140px]">Category</TableHead>
                  {data.months.map((m) => (
                    <TableHead key={m.month} className="text-right text-xs whitespace-nowrap">{m.month.slice(5)}</TableHead>
                  ))}
                  <TableHead className="text-right font-bold">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pivotCategories.length === 0 ? (
                  <TableRow><TableCell colSpan={14} className="text-center text-xs text-slate-500 py-6 italic">No transactions.</TableCell></TableRow>
                ) : pivotCategories.map((cat) => {
                  const rowTotal = data.months.reduce((s, m) => s + pivotCell(cat, m), 0);
                  return (
                    <TableRow key={cat}>
                      <TableCell className="font-medium">{cat}</TableCell>
                      {data.months.map((m) => {
                        const v = pivotCell(cat, m);
                        return (
                          <TableCell key={m.month} className={`text-right font-mono text-xs ${v > 0 ? 'text-slate-900' : 'text-slate-300'}`}>
                            {v > 0 ? fmtR(v) : '—'}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-right font-mono font-bold">{fmtR(rowTotal)}</TableCell>
                    </TableRow>
                  );
                })}
                <TableRow className="bg-slate-50">
                  <TableCell className="font-bold">Total</TableCell>
                  {data.months.map((m) => (
                    <TableCell key={m.month} className="text-right font-mono font-bold">{m.total > 0 ? fmtR(m.total) : '—'}</TableCell>
                  ))}
                  <TableCell className="text-right font-mono font-extrabold">{fmtR(data.grand_total)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
